import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';

/**
 * List-page filter state that survives navigation and reloads, per user:
 *  - restored instantly from localStorage (excubia:filters:<uid>:<pageKey>)
 *  - saved (debounced) to the user's profile preferences.listFilters.<pageKey>
 *    so the same filters follow the user to other devices
 *
 * usePersistedFilters('detections', { search: '', status: [], severity: [], sort: '-detectionTime', rowsPerPage: 20 })
 *   -> { filters, setFilter(key, value), setFilters(partial), reset(), ready }
 */
const storageKey = (uid, pageKey) => `excubia:filters:${uid || 'anon'}:${pageKey}`;

const readLocal = (key) => { try { const raw = window.localStorage.getItem(key); return raw ? JSON.parse(raw) : null; } catch (e) { return null; } };
const writeLocal = (key, value) => { try { window.localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* private mode / quota */ } };

const sanitise = (value, defaults) => {
  if (!value || typeof value !== 'object') return { ...defaults };
  const out = { ...defaults };
  for (const k of Object.keys(defaults)) {
    if (value[k] === undefined) continue;
    if (Array.isArray(defaults[k])) out[k] = Array.isArray(value[k]) ? value[k].map(String) : [];
    else if (typeof defaults[k] === 'number') out[k] = Number.isFinite(Number(value[k])) ? Number(value[k]) : defaults[k];
    else out[k] = value[k] === null ? defaults[k] : value[k];
  }
  return out;
};

export default function usePersistedFilters(pageKey, defaults) {
  const { user } = useAuth();
  const uid = user?._id || user?.id || null;
  const key = storageKey(uid, pageKey);
  const defaultsRef = useRef(defaults);

  const initial = useMemo(() => {
    const local = readLocal(key);
    const remote = user?.preferences?.listFilters?.[pageKey];
    return sanitise(local || remote, defaultsRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const [filters, setFiltersState] = useState(initial);
  const saveTimer = useRef(null);
  const lastSaved = useRef(JSON.stringify(initial));

  // Persist: localStorage immediately, profile after a short debounce
  useEffect(() => {
    writeLocal(key, filters);
    const serialised = JSON.stringify(filters);
    if (serialised === lastSaved.current || !uid) return undefined;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      lastSaved.current = serialised;
      api.put('/auth/profile', { preferences: { listFilters: { [pageKey]: filters } } }).catch(() => {});
    }, 1500);
    return () => clearTimeout(saveTimer.current);
  }, [filters, key, pageKey, uid]);

  const setFilter = useCallback((k, value) => setFiltersState((prev) => (prev[k] === value ? prev : { ...prev, [k]: value })), []);
  const setFilters = useCallback((partial) => setFiltersState((prev) => ({ ...prev, ...partial })), []);
  const reset = useCallback(() => setFiltersState({ ...defaultsRef.current }), []);
  const isDefault = useMemo(() => JSON.stringify(filters) === JSON.stringify(defaultsRef.current), [filters]);

  return { filters, setFilter, setFilters, reset, isDefault, ready: true };
}
